import InstallPrompt from "@/components/InstallPrompt";
import { NavUser } from "@/components/nav-user";
import { Separator } from "@/components/ui/separator";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { createFileRoute, Link, Navigate, Outlet, useMatchRoute } from "@tanstack/react-router";
import { Home, type LucideIcon } from "lucide-react";

export const Route = createFileRoute("/dashboard/customer")({
  component: RouteComponent,
});

function RouteComponent() {
  const { user, userProfile, loading, profileLoading } = useAuth();

  // Show loading spinner while auth is loading
  if (loading || profileLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-32 w-32 animate-spin rounded-full border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  // Redirect if not authenticated or not a customer
  if (!user || userProfile?.type !== "customer") {
    return <Navigate to="/login" />;
  }

  // Show error if access is revoked or account is deleted
  if (userProfile?.accessRevoked || userProfile?.isDeleted === true) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="max-w-md text-center">
          <h1 className="mb-4 text-4xl font-bold text-gray-900">Access Denied</h1>
          <p className="mb-6 text-lg text-gray-600">
            Your account access has been {userProfile?.isDeleted ? "deactivated" : "revoked"}.
            Please contact your administrator for assistance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 data-[orientation=vertical]:h-4" />
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-0 md:p-2 lg:p-4">
          <Outlet />
          <InstallPrompt />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const data = {
    navMain: [
      {
        title: "Home",
        url: "/dashboard/customer",
        icon: Home,
      },
    ],
  };

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader className="flex flex-col items-center justify-center">
        <Link to="/dashboard/customer" className="flex flex-col items-center">
          <img src="/mainLogo.svg" alt="GetHandle" className="py-8" />
          <span className="text-[10px] text-gray-400 leading-none -mt-5 mb-3 tracking-wide">
            v{APP_VERSION}
          </span>
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={data.navMain} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

function NavMain({
  items,
}: {
  items: {
    title: string;
    url: string;
    icon?: LucideIcon;
  }[];
}) {
  const matchRoute = useMatchRoute();

  return (
    <SidebarMenu>
      {items.map((item) => {
        const isItemActive = matchRoute({ to: item.url });

        return (
          <SidebarMenuItem key={item.title}>
            <SidebarMenuButton tooltip={item.title} asChild isActive={!!isItemActive}>
              <LinkWithSidebarClose to={item.url} preload="viewport">
                {item.icon && <item.icon />}
                <span>{item.title}</span>
              </LinkWithSidebarClose>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );
}

function LinkWithSidebarClose({ to, children, ...props }: React.ComponentProps<typeof Link>) {
  const { isMobile, setOpenMobile } = useSidebar();

  const handleClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Link to={to} onClick={handleClick} {...props}>
      {children}
    </Link>
  );
}
